/**
 * Тарифы Лайт и Про: срок по журналу выдач (docs/PAYMENTS.md, §4.2).
 *
 * Состояние подписки нигде не хранится — оно вычисляется из выдач: покупок,
 * продлений и решений администратора. Поэтому оно не может разойтись с
 * историей, а отзыв одной выдачи (возврат денег, ошибка) честно пересчитывает
 * всё, что было после неё.
 *
 * Правила согласованы 13.10.2026:
 *  - повторная покупка того же тарифа продлевает его от текущей даты окончания;
 *  - Про при действующем Лайт: Лайт прекращается, остаток не переносится и не
 *    компенсируется, Про действует с этого момента;
 *  - Лайт при действующем Про запрещён;
 *  - администратор может установить «тариф до даты» или снять тариф — это
 *    перекрывает всё прежнее.
 *
 * Чистые функции с явным `now`: сервер, админка и тесты считают одинаково.
 */

export const SUBSCRIPTION_PLANS = ['light', 'pro'] as const;
export type SubscriptionPlan = (typeof SUBSCRIPTION_PLANS)[number];

export const SUBSCRIPTION_PLAN_LABELS: Record<SubscriptionPlan, string> = {
  light: 'Лайт',
  pro: 'Про',
};

/** Срок тарифа по концепции. Продукт может задать другой — это лишь значение по умолчанию. */
export const DEFAULT_PLAN_DAYS = 30;

/** Срок предложения: календарные годы либо фиксированное число дней. */
export function formatPlanDuration(days: number | null, years?: number | null): string {
  if (!years) return `${days} дн.`;
  const last = years % 10;
  const teen = years % 100 >= 11 && years % 100 <= 14;
  return `${years} ${!teen && last === 1 ? 'год' : !teen && last >= 2 && last <= 4 ? 'года' : 'лет'}`;
}

function durationEnd(start: number, days: number, years?: number | null): number {
  if (!years) return start + days * MS_PER_DAY;
  const end = new Date(start);
  const month = end.getUTCMonth();
  end.setUTCFullYear(end.getUTCFullYear() + years);
  // 29 февраля в невисокосном году заканчивается 28 февраля, а не 1 марта.
  if (end.getUTCMonth() !== month) end.setUTCDate(0);
  return end.getTime();
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Одна выдача тарифа — строка `entitlements` типа `subscription`. */
export type PlanGrant = {
  id: string;
  /** `none` — администратор снял тариф. */
  plan: SubscriptionPlan | 'none';
  /** Продление на N дней (покупка или администратор). */
  days: number | null;
  /** Календарные годы вместо дней; сохраняются для пересчёта при возврате. */
  years?: number | null;
  /** «Тариф до даты» — только администратор. */
  endsAt: string | null;
  grantedAt: string;
};

/**
 * Что сделала выдача:
 * `started` — тариф начался; `extended` — продлён от даты окончания;
 * `upgraded` — Лайт сменился на Про; `set` — администратор задал срок;
 * `cancelled` — администратор снял тариф; `skipped` — не применена
 * (Лайт при действующем Про).
 */
export type PlanEffect = 'started' | 'extended' | 'upgraded' | 'set' | 'cancelled' | 'skipped';

export type PlanStep = {
  grantId: string;
  effect: PlanEffect;
  /** Тариф и окончание сразу после этой выдачи. */
  plan: SubscriptionPlan | null;
  endsAt: string | null;
  /** Сколько дней сгорело: при переходе на Про или при снятии тарифа. */
  lostDays: number;
};

export type PlanState = {
  /** Действующий тариф; `null`, если тарифа нет или он закончился. */
  plan: SubscriptionPlan | null;
  active: boolean;
  /** Окончание действующего тарифа, а если он закончился — последнего. */
  endsAt: string | null;
  /** Тариф, который закончился (для «Лайт закончился 01.10»). */
  lastPlan: SubscriptionPlan | null;
  expired: boolean;
  /** Целых дней до окончания, с округлением вверх. */
  daysLeft: number | null;
};

type Cursor = { plan: SubscriptionPlan | null; end: number | null };

export type ExtensionResult =
  | { allowed: true; effect: 'started' | 'extended' | 'upgraded'; plan: SubscriptionPlan; end: number; lostDays: number }
  | { allowed: false; reason: 'light_during_pro' };

/**
 * Продление на `days` дней в момент `at` поверх текущего состояния.
 * Единственное место, где живут правила сроков: им пользуются и пересчёт
 * журнала, и предварительный расчёт в админке, и (на этапе 2) обработка оплат.
 */
export function applyExtension(
  current: Cursor,
  plan: SubscriptionPlan,
  days: number,
  at: number,
  years?: number | null,
): ExtensionResult {
  const active = current.plan !== null && current.end !== null && current.end > at;

  if (active && current.plan === plan) {
    return { allowed: true, effect: 'extended', plan, end: durationEnd(current.end!, days, years), lostDays: 0 };
  }
  if (active && current.plan === 'light' && plan === 'pro') {
    const lostDays = Math.ceil((current.end! - at) / MS_PER_DAY);
    return { allowed: true, effect: 'upgraded', plan, end: durationEnd(at, days, years), lostDays };
  }
  if (active && current.plan === 'pro' && plan === 'light') {
    return { allowed: false, reason: 'light_during_pro' };
  }
  return { allowed: true, effect: 'started', plan, end: durationEnd(at, days, years), lostDays: 0 };
}

/**
 * Проигрывает журнал выдач по порядку и возвращает итог и шаг каждой выдачи.
 * Отозванные выдачи сюда не передаются — в этом и состоит пересчёт.
 */
export function replayPlanGrants(grants: PlanGrant[]): { cursor: Cursor; steps: PlanStep[] } {
  /*
    Снятие тарифа в тот же момент идёт раньше выдачи: так записан переход
    Лайт → Про — сначала «Лайт прекращён», потом «Про +30 дней».
  */
  const ordered = [...grants].sort(
    (a, b) =>
      a.grantedAt.localeCompare(b.grantedAt) ||
      Number(b.plan === 'none') - Number(a.plan === 'none') ||
      a.id.localeCompare(b.id),
  );

  let cursor: Cursor = { plan: null, end: null };
  const steps: PlanStep[] = [];

  for (const grant of ordered) {
    const at = Date.parse(grant.grantedAt);
    let effect: PlanEffect = 'skipped';
    let lostDays = 0;

    if (grant.plan === 'none') {
      // Снять тариф: действующий заканчивается сейчас, закончившийся остаётся как был.
      if (cursor.plan !== null && cursor.end !== null && cursor.end > at) {
        lostDays = Math.ceil((cursor.end - at) / MS_PER_DAY);
        cursor = { plan: cursor.plan, end: at };
      }
      effect = 'cancelled';
    } else if ((grant.days ?? 0) > 0 || (grant.years ?? 0) > 0) {
      const result = applyExtension(cursor, grant.plan, grant.days ?? 0, at, grant.years);
      if (result.allowed) {
        cursor = { plan: result.plan, end: result.end };
        effect = result.effect;
        lostDays = result.lostDays;
      }
    } else if (grant.endsAt !== null) {
      cursor = { plan: grant.plan, end: Date.parse(grant.endsAt) };
      effect = 'set';
    }

    steps.push({
      grantId: grant.id,
      effect,
      plan: cursor.plan,
      endsAt: cursor.end === null ? null : new Date(cursor.end).toISOString(),
      lostDays,
    });
  }

  return { cursor, steps };
}

/** Состояние на момент `now` — по выдачам, сделанным не позже него. */
export function computePlanState(grants: PlanGrant[], now: Date = new Date()): PlanState {
  return stateOf(replayPlanGrants(grantedBy(grants, now)).cursor, now);
}

/** Что даст продление прямо сейчас — для предупреждения перед повышением тарифа. */
export function previewExtension(
  grants: PlanGrant[],
  plan: SubscriptionPlan,
  days: number,
  now: Date = new Date(),
  years?: number | null,
):
  | { allowed: true; effect: 'started' | 'extended' | 'upgraded'; endsAt: string; lostDays: number }
  | { allowed: false; reason: 'light_during_pro' } {
  const result = applyExtension(replayPlanGrants(grantedBy(grants, now)).cursor, plan, days, now.getTime(), years);
  if (!result.allowed) return result;
  return {
    allowed: true,
    effect: result.effect,
    endsAt: new Date(result.end).toISOString(),
    lostDays: result.lostDays,
  };
}

/** Строка, которую нужно записать в журнал выдач. */
export type PlanGrantOp =
  | { plan: 'none'; reason: 'upgrade' }
  | { plan: SubscriptionPlan; days: number | null; years?: number };

/**
 * Что записать в журнал, чтобы продлить или выдать тариф (решение 14.10.2026).
 *
 * Переход Лайт → Про — две строки в один момент: «Лайт прекращён» и «Про +N
 * дней». Прекращение Лайт — отдельная запись, а не следствие выдачи Про:
 * отзыв или возврат Про его не отменяет, остаток Лайт сам не возвращается.
 * Вернуть Лайт можно только отдельным действием администратора — отзывом
 * строки «Лайт прекращён» с причиной.
 */
export function planExtensionOps(
  grants: PlanGrant[],
  plan: SubscriptionPlan,
  days: number,
  now: Date = new Date(),
  years?: number | null,
):
  | {
      allowed: true;
      effect: 'started' | 'extended' | 'upgraded';
      endsAt: string;
      lostDays: number;
      ops: PlanGrantOp[];
    }
  | { allowed: false; reason: 'light_during_pro' } {
  const preview = previewExtension(grants, plan, days, now, years);
  if (!preview.allowed) return preview;
  const grant = years ? { plan, days: null, years } : { plan, days };
  const ops: PlanGrantOp[] =
    preview.effect === 'upgraded'
      ? [{ plan: 'none', reason: 'upgrade' }, grant]
      : [grant];
  return { ...preview, ops };
}

function grantedBy(grants: PlanGrant[], now: Date): PlanGrant[] {
  const limit = now.getTime();
  return grants.filter((grant) => Date.parse(grant.grantedAt) <= limit);
}

function stateOf(cursor: Cursor, now: Date): PlanState {
  if (cursor.plan === null || cursor.end === null) {
    return { plan: null, active: false, endsAt: null, lastPlan: null, expired: false, daysLeft: null };
  }

  const remaining = cursor.end - now.getTime();
  const active = remaining > 0;
  return {
    plan: active ? cursor.plan : null,
    active,
    endsAt: new Date(cursor.end).toISOString(),
    lastPlan: active ? null : cursor.plan,
    expired: !active,
    daysLeft: active ? Math.ceil(remaining / MS_PER_DAY) : null,
  };
}
